import { ApDocumentStatus } from '../ap-document/ap-document';
import { Currency } from '../bank-account/bank-account';

/** Global entity definition code for a withholding document — its fiscal authorization is keyed by it (#4800). */
export const WITHHOLDING_DOCUMENT_ENTITY_CODE = 60;

/**
 * What kind of holdback this is (#4800). Only `Tax` is a fiscal document: it draws a Constancia correlativo
 * and is remitted to SAR. A `Guarantee` holdback is a contractual amount owed back to the provider.
 */
export enum WithholdingCategory {
  Tax = 1,
  Guarantee = 2,
}

/**
 * A reference to another document (id + number + entity code).
 *
 * Mirrors the API's `FinancialEntityKey` (#4893): a financial reference also carries the numbers a human
 * reconciles by, so a screen showing the reference doesn't have to fetch the referenced document just to
 * name it the way the paper does. Stamped at issue time — references written before that read as undefined.
 */
export interface WithholdingEntityKey {
  reference?: string | null;
  number?: string | null;
  entityDefinitionCode?: number | null;
  /** The referenced document's fiscal (CAI) number — what the provider printed on it. */
  fiscalNumber?: string | null;
  /** The provider's own ERP number, where the source distinguishes it from the fiscal one. */
  providerReferenceNumber?: string | null;
}

/**
 * One AP document a certificate withholds from (#5122).
 *
 * Everything here is a fact about the DOCUMENT rather than the certificate: the control account it was
 * booked against, the currency it was billed in, and the two rates that convert it. A certificate gathering
 * three invoices relieves three payables, each on its own terms.
 */
export interface WithholdingDocumentSource {
  /** The AP document — id, our number, and the fiscal number the certificate legally cites. */
  document?: WithholdingEntityKey | null;

  apAccountReference?: string | null;
  apAccountNumber?: string | null;

  /** The currency the AP document was billed in. */
  currency: Currency;

  /** Source currency → functional (USD), inherited from the document so the payable is relieved at the rate
   * it was credited at. */
  exchangeRate: number;

  /**
   * Source currency → LEMPIRA, at the rate in force on the AP document's own issue date. This is the number
   * printed on the Constancia beside the amount, and the officer may correct it. 1 for a Lempira document.
   */
  localRate: number;
  rateSource?: number | null;

  /** The date `localRate` was quoted FOR — the AP document's own issue date on a source added normally.
   *  Shown beside the rate so a certificate carrying several rates reads as invoices issued on different
   *  days rather than as an inconsistency. Null on sources written before this existed. */
  rateDate?: string | null;

  /** Withheld from this document, in its own currency — what its payable is reduced by. */
  withheldAmount: number;
  functionalAmount: number;
  /** Withheld from this document in Lempira — its share of what is owed to SAR. */
  localWithheldAmount: number;

  lines: WithholdingDocumentLine[];
}

/**
 * Constancia de Retención (#4800/#5122) — its own document, no longer an ApDocument.
 *
 * A certificate is defined by its provider and ONE withholding code; the AP documents it covers hang off
 * `sources`. A locally domiciled provider's certificate covers exactly one document; a foreign provider's
 * may cover several.
 */
export interface WithholdingDocument {
  id?: string | null;
  number: string;
  category: WithholdingCategory;
  documentDate: string;
  postingDate: string;
  fiscalYear: number;
  fiscalPeriod: number;

  providerReference?: string | null;
  /** Hydrated by the API for display; not stored on the document. */
  providerCode: string;
  providerName: string;
  oneTimeVendorName?: string | null;
  providerTaxId?: string | null;

  financialCompanyReference: string;
  /** OUR jurisdiction — always HND. Not the provider's domicile. */
  withholdingCountryCode?: string | null;
  withholdingResidency?: number | null;

  /** The PROVIDER's domicile, snapshotted at creation (#5122). */
  providerCountryCode?: string | null;
  /** Frozen at creation: true → this certificate covers exactly one AP document. */
  isLocalProvider: boolean;

  /** The single tax this certificate withholds, chosen at the header (#5122). */
  withholdingCode?: string | null;
  /** Rate snapshot (%) frozen from the catalog when the code was chosen. */
  withholdingRate?: number | null;

  /** The AP documents this certificate withholds from (#5122). */
  sources: WithholdingDocumentSource[];

  /** The first source, kept while readers move to `sources`. Names only the first on a multi-document
   * certificate — prefer `sources`. */
  invoiceReference?: WithholdingEntityKey | null;

  /** The total owed to SAR, in `currency` — Lempira on anything issued from #5122 onward. */
  totalAmount: number;
  functionalAmount?: number | null;
  /** Always HNL on certificates issued from #5122 onward: SAR is paid in Lempira. */
  currency: Currency;
  functionalCurrency: Currency;
  exchangeRate: number;
  rateSource?: number | null;

  apAccountReference?: string | null;
  apAccountNumber?: string | null;
  journalEntryReference?: string | null;
  journalEntryDocumentNumber?: string | null;

  /** The certificate block — populated on post, and only for `Tax`. Null while draft, or when the company
   * has no Constancia authorization registered yet. */
  certificateNumber?: string | null;
  cai?: string | null;
  taxAuthorizationReference?: string | null;
  issuedDate?: string | null;

  /** The remittance that paid this to SAR, and when. Null until remitted. */
  withholdingRemittanceReference?: string | null;
  remittedDate?: string | null;

  status: ApDocumentStatus;
  notes?: string | null;
  /** Every line across every source, flattened. The grouping that matters for display is `sources`. */
  lineItems: WithholdingDocumentLine[];

  createdBy?: string | null;
  createdDate?: string | null;
  modifiedBy?: string | null;
  modifiedDate?: string | null;
}

/** One withheld line: base × the certificate's rate% = amount, in the source's currency and in Lempira. */
export interface WithholdingDocumentLine {
  lineNumber: number;
  /** The AP document this line belongs to — set on flattened reads, absent inside a source's own lines. */
  sourceDocumentReference?: string | null;
  /** The AP document line this withholds against — with the document, the payment gate's coverage key. */
  sourceLineNumber?: number | null;
  description?: string | null;
  itemCode?: string | null;
  itemClassificationCode?: string | null;
  /** The withholding base — the AP document line's amount, in the source's currency. */
  baseAmount: number;
  withholdingCode?: string | null;
  withholdingRate?: number | null;
  amount: number;
  /** The base in Lempira — baseAmount × the source's localRate. */
  localBaseAmount: number;
  /** The withheld amount in Lempira — what reaches the Constancia and the remittance. */
  localAmount: number;
  /**
   * "No aplica": the line sits on the certificate but nothing is withheld from it. It still COVERS the line,
   * so the AP document can be paid — which is the whole difference between exempting a line and leaving it
   * off the certificate.
   */
  isExempt: boolean;
}

/** Search filter for the withholding-certificates screen. */
export interface WithholdingDocumentSearchCommand {
  providerReference?: string | null;
  number?: string | null;
  certificateNumber?: string | null;
  /** The tax withheld, now that a certificate has exactly one (#5122). */
  withholdingCode?: string | null;
  /** Certificates citing one AP document — "what has been withheld from this invoice?". */
  sourceDocumentReference?: string | null;
  category?: WithholdingCategory | null;
  status?: ApDocumentStatus | null;
  currency?: Currency | null;
  documentDateFrom?: string | null;
  documentDateTo?: string | null;
  remittedOnly?: boolean | null;
  pendingRemittanceOnly?: boolean | null;
}

/**
 * Opens a draft (#5122). The two things that decide which AP documents may share a certificate — the
 * provider and the ONE tax — are fixed before any document is attached.
 */
export interface WithholdingDocumentCreateCommand {
  providerReference: string;
  withholdingCode: string;
  documentDate?: string | null;
  postingDate?: string | null;
  notes?: string | null;
}

/** Attaches AP documents to a draft; their unclaimed service lines are seeded server-side. */
export interface WithholdingAddSourcesCommand {
  documentReferences: string[];
}

/**
 * Save payload (#5122). The server re-resolves the header code's rate and recomputes every amount.
 *
 * A line omitted from its source block is REMOVED from the certificate, releasing it to another code; a line
 * marked exempt stays covered and withholds nothing. A source omitted from `sources` entirely is left alone.
 */
export interface WithholdingSaveCommand {
  documentDate?: string | null;
  postingDate?: string | null;
  notes?: string | null;
  sources: WithholdingSourceCommand[];
}

export interface WithholdingSourceCommand {
  documentReference: string;
  /** Officer's correction to the source → Lempira rate. Null keeps the rate stamped when it was attached. */
  localRate?: number | null;
  lines: WithholdingLineCommand[];
}

export interface WithholdingLineCommand {
  /** The LineNumber of the AP document line this withholding applies to. */
  sourceLineNumber: number;
  /** "No aplica": keep the line covered so the document can be paid, but withhold nothing from it. */
  isExempt: boolean;
}

/**
 * A line of an AP document that another POSTED certificate already withholds (#5122).
 *
 * Shown as a locked row so a line's absence is explained rather than merely observed: the officer removes a
 * line so another certificate can claim it under a different code, and when they later restore the source's
 * lines the claimed ones do not come back. The certificate number says where the line went.
 */
export interface WithholdingClaimedLine {
  sourceLineNumber: number;
  description?: string | null;
  baseAmount: number;
  localBaseAmount: number;
  certificateNumber?: string | null;
  certificateReference?: string | null;
}
